/**
 * Batch Processes List Invariants
 *
 * Enforces:
 * 1. Normalization - page clamping to valid range
 * 2. Permissions - source-based command access control
 * 3. Validation - null (no form validation for list)
 */

import { Invariants } from '@commandDrivenStateEngine/core/invariants';
import { BatchProcessesListState } from './state';
import { BatchProcessesListCommand } from './commands';
import { selectors } from './state';

/**
 * Normalize state for consistency.
 *
 * - Clamps `currentPage` to valid range (1 to totalHistoryPages)
 */
const normalize = ( state: BatchProcessesListState ): BatchProcessesListState => {
  let normalized = { ...state };

  const totalPages = selectors.totalHistoryPages( normalized );

  if ( totalPages > 0 && normalized.currentPage > totalPages ) {
    normalized = { ...normalized, currentPage: totalPages };
  }
  if ( normalized.currentPage < 1 ) {
    normalized = { ...normalized, currentPage: 1 };
  }

  return normalized;
};

/**
 * Permission control - source-based command access.
 *
 * Permission matrix:
 * - user: pagination/* (setPage, setPerPage)
 * - api: data/setHistory, loading/setHistoryLoading
 * - ai: nothing
 * - url: nothing
 * - application: ALL
 */
const canApply = ({
  command,
  meta
}: {
  state: BatchProcessesListState;
  command: BatchProcessesListCommand;
  meta: import( '@commandDrivenStateEngine/core/types' ).CommandMeta;
}): boolean => {
  const { source } = meta;
  const { type } = command;

  // Application source: can dispatch ALL commands
  if ( source === 'application' ) {
    return true;
  }

  // API source: data and loading commands
  if ( source === 'api' ) {
    return (
      type.startsWith( 'batchProcesses/list/data/' ) ||
      type.startsWith( 'batchProcesses/list/loading/' )
    );
  }

  // User source: pagination commands only
  if ( source === 'user' ) {
    return type.startsWith( 'batchProcesses/list/pagination/' );
  }

  // AI source: nothing
  if ( source === 'ai' ) {
    return false;
  }

  // URL source: nothing
  if ( source === 'url' ) {
    return false;
  }

  // Unknown sources: allow by default (don't block)
  return true;
};

/**
 * No form validation needed for list view.
 */
const validate = ( _state: BatchProcessesListState ): Record<string, string> | null => {
  return null;
};

export const invariants: Invariants<BatchProcessesListState, BatchProcessesListCommand> = {
  normalize,
  canApply,
  validate
};
